// Header-section editor for the print-template builder.
//
// Edits the `HeaderConfig` slice of `PrintTemplateContent` (plan §4.2).
// Phase 5 wires this; the variable palette is added in Phase 6 — for
// now title / subtitle / company-line fields are plain textareas.
//
// Rules:
//   - "Show logo" is disabled (with a tooltip) when the company has
//     no logo_url. The Phase 9 LogoRequiredModal layers on top of
//     this for the case where show=true but logo_url=null.
//   - Company lines: 0..6 entries; add / remove / reorder via row
//     controls. Each line is rendered in the printed header on its
//     own row, with empty-token-only lines dropped at render time.
//   - Alignment: left / center / right segmented control.

import { Plus, X } from 'lucide-react';

import { Button } from '../../../../components/ui/Button/Button';
import { Tooltip } from '../../../../components/ui/Tooltip/Tooltip';
import { VariablePalette } from './VariablePalette';
import { getVariablesForType } from '../variables';
import type {
  HeaderConfig,
  PrintTemplateType,
} from '../../../../types/api';
import styles from './HeaderEditor.module.css';

const ALIGNMENTS: ReadonlyArray<{ value: 'left' | 'center' | 'right'; label: string }> = [
  { value: 'left', label: 'Left' },
  { value: 'center', label: 'Center' },
  { value: 'right', label: 'Right' },
];

const MAX_COMPANY_LINES = 6;

export interface HeaderEditorProps {
  value: HeaderConfig;
  onChange: (next: HeaderConfig) => void;
  /** Resolved at the page level from useCompanyOverview(). */
  companyHasLogo: boolean;
  /** Used to select the right variable list for the palette. */
  templateType: PrintTemplateType;
}

export function HeaderEditor({
  value,
  onChange,
  companyHasLogo,
  templateType,
}: HeaderEditorProps): JSX.Element {
  const paletteVariables = getVariablesForType(templateType);
  function update<K extends keyof HeaderConfig>(key: K, v: HeaderConfig[K]) {
    onChange({ ...value, [key]: v });
  }

  function setLogoShow(next: boolean) {
    onChange({ ...value, logo: { ...value.logo, show: next } });
  }

  function setLogoHeight(next: number) {
    const clamped = Math.min(30, Math.max(6, Math.round(next)));
    onChange({ ...value, logo: { ...value.logo, max_height_mm: clamped } });
  }

  function setLine(index: number, next: string) {
    const lines = [...value.company_lines];
    lines[index] = next;
    onChange({ ...value, company_lines: lines });
  }

  function addLine() {
    if (value.company_lines.length >= MAX_COMPANY_LINES) return;
    onChange({
      ...value,
      company_lines: [...value.company_lines, ''],
    });
  }

  function removeLine(index: number) {
    onChange({
      ...value,
      company_lines: value.company_lines.filter((_, i) => i !== index),
    });
  }

  return (
    <section className={styles.section} aria-label="Header configuration">
      <header className={styles.head}>
        <h3 className={styles.title}>Header</h3>
        <label className={styles.toggle}>
          <input
            type="checkbox"
            checked={value.show}
            onChange={(e) => update('show', e.target.checked)}
          />
          <span>Show header</span>
        </label>
      </header>

      {value.show ? (
        <div className={styles.body}>
          {/* Logo row */}
          <div className={styles.row}>
            <Tooltip
              content="Upload a logo on Company Settings before enabling this option."
              disabled={companyHasLogo}
            >
              <label className={styles.toggle}>
                <input
                  type="checkbox"
                  checked={value.logo.show && companyHasLogo}
                  disabled={!companyHasLogo}
                  onChange={(e) => setLogoShow(e.target.checked)}
                />
                <span>Show logo</span>
              </label>
            </Tooltip>

            <label className={styles.numberLabel}>
              Logo max height (mm)
              <input
                type="number"
                min={6}
                max={30}
                step={1}
                disabled={!value.logo.show || !companyHasLogo}
                value={value.logo.max_height_mm}
                onChange={(e) => setLogoHeight(Number(e.target.value))}
                className={styles.numberInput}
              />
            </label>
          </div>

          {/* Title + subtitle (with variable palette) */}
          <VariablePalette
            label="Title template"
            value={value.title_template}
            onChange={(next) => update('title_template', next)}
            variables={paletteVariables}
            rows={2}
            maxLength={500}
            placeholder="Diary — {{driver_name}}"
          />

          <VariablePalette
            label="Subtitle template"
            value={value.subtitle_template}
            onChange={(next) => update('subtitle_template', next)}
            variables={paletteVariables}
            rows={2}
            maxLength={500}
            placeholder="Week of {{week_start_date}} – {{week_end_date}}"
          />

          {/* Company lines (each with variable palette) */}
          <div className={styles.fieldLabel}>
            <span>Company lines</span>
            <ul className={styles.lineList}>
              {value.company_lines.map((line, idx) => (
                <li key={idx} className={styles.lineRow}>
                  <div className={styles.lineField}>
                    <VariablePalette
                      label={`Company line ${idx + 1}`}
                      value={line}
                      onChange={(next) => setLine(idx, next)}
                      variables={paletteVariables}
                      rows={1}
                      maxLength={500}
                      placeholder="{{company_name}}"
                    />
                  </div>
                  <Button
                    variant="icon"
                    size="sm"
                    icon={X}
                    onClick={() => removeLine(idx)}
                    aria-label={`Remove line ${idx + 1}`}
                  />
                </li>
              ))}
            </ul>
            <Button
              variant="ghost"
              size="sm"
              icon={Plus}
              onClick={addLine}
              disabled={value.company_lines.length >= MAX_COMPANY_LINES}
            >
              Add company line
            </Button>
          </div>

          {/* Alignment */}
          <div className={styles.fieldLabel}>
            <span>Alignment</span>
            <div className={styles.segmented} role="group" aria-label="Header alignment">
              {ALIGNMENTS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  className={
                    value.alignment === opt.value
                      ? `${styles.segItem} ${styles.segItemActive}`
                      : styles.segItem
                  }
                  aria-pressed={value.alignment === opt.value}
                  onClick={() => update('alignment', opt.value)}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      ) : null}
    </section>
  );
}
